import React, { useState, useEffect, useContext, useCallback } from "react";
import { RecaptchaVerifier, multiFactor, ConfirmationResult, MultiFactorSession, PhoneAuthProvider } from "firebase/auth";
import { auth } from "../../../../firebase/firebase";
import { SignupContext } from "../../../../context/SignupContext";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import CountryCode from "./CountryCode";
import Button from "../../../../components/Button";
import LoadingPopup from "../../../channels/create/LoadingPopup";
const { phone: validPhone } = require("phone");

declare global {
  interface Window {
    recaptchaVerifier: RecaptchaVerifier | undefined;
  }
}

const Step1 = () => {
  const navigate = useNavigate();
  const { formData, updateFormData } = useContext(SignupContext);
  const [loading, setLoading] = useState(false);
  const { phone, phoneVerificationId } = formData;
  const [selectedCode, setSelectedCode] = useState("+1");
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({ phone: "" });
  const [isFormValid, setIsFormValid] = useState(false);
  const [phoneNo, setPhoneNo] = useState("");

  const [captchaVerifier, setCaptchaVerifier] = useState<RecaptchaVerifier | null>(null);
  const [multiFactorSession, setMultiFactorSession] = useState<MultiFactorSession | null>(null);

  const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;
  // const schema = Yup.object().shape({
  //   phone: Yup.string().required("Phone number required").matches(phoneRegExp, "Phone number is not valid").min(10, "too short").max(10, "too long"),
  // });

  const schema = Yup.object().shape({
    phone: Yup.string()
      .required("Phone number is required")
      .test("is-valid-phone", "Invalid phone number", value => {
        if (!value) return false; // Required validation
        const result = validPhone(value);
        return result.isValid; // Ensure phone() returns a valid number
      }),
  });

  useEffect(() => {
    console.log("###verificationId:", formData.phoneVerificationId);
  }, [formData]);

  useEffect(() => {
    let recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
      size: "invisible",
      callback: (response: any) => {
        console.log(response);
      },
    });

    if (auth.currentUser) {
      multiFactor(auth.currentUser)
        .getSession()
        .then(function (session) {
          setMultiFactorSession(session);
        });
    }
    setCaptchaVerifier(recaptchaVerifier);
  }, []);

  const sendOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (captchaVerifier) {
      setLoading(true);
      const phoneInfoOptions = {
        phoneNumber: phone,
        session: multiFactorSession,
      };

      const phoneAuthProvider = new PhoneAuthProvider(auth);

      const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, captchaVerifier);
      const newVal = { ...formData, phoneVerificationId: verificationId };
      updateFormData(newVal);
      setTimeout(() => {
        setLoading(false);
        navigate("../verifyOtp");
      }, 2000);
    }
  };

  const updateValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const phNumber = selectedCode + value;

    const newVal = { ...formData, [name]: phNumber };
    updateFormData(newVal);
    setPhoneNo(value);
  };

  const onInputEvent = useCallback(
    async (event: React.FocusEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (event.type === "focus") {
        setErrors(prev => {
          const { [name]: _, ...rest } = prev;
          return rest;
        });
        return;
      }
      try {
        await schema.validateAt(name, { [name]: selectedCode + value });
        setErrors(prev => {
          const { [name]: _, ...rest } = prev;
          return rest;
        });
        return true;
      } catch (err) {
        let error = err as Yup.ValidationError;
        setErrors(prev => ({ ...prev, [name]: error.message }));
      }
    },
    [selectedCode]
  );

  useEffect(() => {
    const validate = async (data: { [key: string]: string | undefined }) => {
      try {
        await schema.validate(data, { abortEarly: true });
        setIsFormValid(true);
        setErrors({});
      } catch (err) {
        setIsFormValid(false);
      }
    };

    validate({ phone: selectedCode + phoneNo });
  }, [phoneNo, selectedCode]);

  return (
    <>
      <section>
        <div className="container">
          <div className="grid grid-cols-1 gap-3 p-5 md:grid-cols-2 gap-x-5 gap-y-5">
            {/* Left Container */}
            <div className="flex flex-col items-start justify-start">
              <h1 className="herotitle">Skarvo</h1>
              <p className="text-xl font-normal">Setup multi-factor authentication to secure your account.</p>
            </div>

            {/* Right Container */}
            <div className="flex flex-col gap-10">
              <LoadingPopup isLoading={loading} loadingText="Processing" />
              <div className="flex flex-col items-start formcard">
                <div className="flex flex-row items-start formheader">
                  <div className="text-xl font-bold text-center mt-[15px] mb-[30px]">Sign Up</div>
                </div>
                <div className="flex justify-center w-full px-[30px] mb-[30px]">
                  <div className=" bg-skseparatorgray w-full rounded-full h-[5px]">
                    <div className=" bg-skgreen h-[5px] rounded-full" style={{ width: "75%" }}></div>
                  </div>
                </div>

                <div className="mb-5 px-[30px] flex flex-col space-y-[10px] w-full">
                  <label htmlFor="phone" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                    Your phone
                  </label>
                  <CountryCode onCountrySelect={code => setSelectedCode(code)} />
                  <input
                    type="text"
                    name="phone"
                    id="phone"
                    className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    onChange={updateValue}
                    placeholder="Phone number"
                    onBlur={onInputEvent}
                    onFocus={onInputEvent}
                    value={phoneNo}
                    required
                  />
                  {errors.phone ? (
                    <span className="block mb-2 text-sm text-red-600 dark:text-red-500">{errors.phone}</span>
                  ) : (
                    <span className="block mb-2 text-sm">{"\u00A0"}</span>
                  )}
                  <span className="block mb-2 text-sm text-gray-400 dark:text-gray-600">
                    Enter your phone number to receive a verification code. Messaging and data rates may apply.
                  </span>
                </div>
                <div className="thickseparator"></div>
                <div className="flex flex-col items-center justify-between w-full p-[30px] md:flex-row-reverse  gap-y-[30px]">
                  <Button to="/" onClick={sendOtp} className="w-full md:w-auto md:min-w-[120px] formprimarybutton" isDisabled={!isFormValid}>
                    Continue
                  </Button>
                  <Link to="/" className="w-full md:w-auto md:min-w-[120px] formsecondarybutton">
                    Cancel
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="recaptcha-container"></div>
      </section>
    </>
  );
};

export default Step1;
